import { createFileRoute } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';

import { PageSkeleton } from '~/components/common/page-skeleton/page-skeleton';
import { navContextSearchSchema } from '~/features/catalog-enrichment/utils/nav-context';
import {
  type QualityChecksSearch,
  qualityChecksSearchSchema,
} from '~/features/catalog-enrichment/utils/quality-checks';

const RunQualityChecksPage = lazy(
  () => import('~/features/catalog-enrichment/pages/run-quality-checks-page'),
);

/** `{ tab, cgRun?, severity?, check?, products?, page?, product?, cursor?, trail? }` + the S8
 *  `from`/`back` trail — every field falls back to a safe default, so a stale link still renders. */
const searchSchema = qualityChecksSearchSchema.extend(navContextSearchSchema);

/**
 * Quality checks: every QC finding and item failure of a batch's content-generation run, with
 * server paging and filters. A sibling of the run's tab routes whose path no tab segment owns, so
 * the batch shell keeps Summary highlighted (the pane card that links here lives on Summary).
 */
export const Route = createFileRoute(
  '/_authenticated/_tenant/catalog-enrichment/runs/$runId/quality-checks',
)({
  validateSearch: searchSchema,
  component: RouteComponent,
});

function RouteComponent() {
  const { runId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  // Tab / filter / page changes replace the history entry, so Back leaves the page.
  const onSearchChange = (patch: Partial<QualityChecksSearch>) => {
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  };

  return (
    <Suspense fallback={<PageSkeleton />}>
      <RunQualityChecksPage runId={runId} search={search} onSearchChange={onSearchChange} />
    </Suspense>
  );
}
